import { useMemo, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle, Code, ExternalLink, Info, Loader2, Package, RefreshCw, ShoppingCart, Store } from "lucide-react";
import { openShopifyTopLevel } from "@/lib/shopifyEmbedded";

interface Props {
  merchantId: string;
  platformType?: string | null;
  shopifyAccessToken?: string | null;
  storeDomain: string;
  webhookSecret?: string | null;
  widgetInstalled?: boolean | null;
  onOpenPreferences?: () => void;
}

export function MerchantConnectorPanel({
  merchantId,
  platformType,
  shopifyAccessToken,
  storeDomain,
  webhookSecret,
  widgetInstalled,
  onOpenPreferences,
}: Props) {
  const isWooCommerceMerchant = platformType === "woocommerce";
  const isShopifyMerchant = platformType === "shopify";
  const [shopifyDomainInput, setShopifyDomainInput] = useState(() => {
    if (isShopifyMerchant && storeDomain?.includes(".myshopify.com")) {
      return storeDomain;
    }

    return "";
  });
  const [connectingShopify, setConnectingShopify] = useState(false);
  const normalizedShopifyDomain = useMemo(
    () => normalizeShopifyDomain(shopifyDomainInput),
    [shopifyDomainInput],
  );

  const handleConnectShopify = () => {
    if (!normalizedShopifyDomain) {
      toast.error("Enter your Shopify store domain");
      return;
    }

    if (!isValidShopifyDomain(normalizedShopifyDomain)) {
      toast.error("Enter a valid Shopify domain like your-store.myshopify.com");
      return;
    }

    setConnectingShopify(true);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim();
      if (!supabaseUrl) {
        throw new Error("Supabase URL is not configured");
      }

      const oauthInitUrl = new URL(`${supabaseUrl}/functions/v1/shopify-oauth-init`);
      oauthInitUrl.searchParams.set("shop", normalizedShopifyDomain);
      oauthInitUrl.searchParams.set("merchant_id", merchantId);
      oauthInitUrl.searchParams.set("source", "dashboard");
      oauthInitUrl.searchParams.set("return_to", "dashboard");
      oauthInitUrl.searchParams.set("signup_source", "direct_website");

      toast.info(
        isWooCommerceMerchant
          ? "Redirecting to Shopify to switch this store connection..."
          : "Redirecting to Shopify to connect your store...",
      );
      openShopifyTopLevel(oauthInitUrl.toString());
    } catch (error: any) {
      console.error("Error starting Shopify OAuth:", error);
      toast.error(error?.message || "Could not start Shopify connection");
      setConnectingShopify(false);
    }
  };

  if (isWooCommerceMerchant) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              WooCommerce Connector
            </CardTitle>
            <CardDescription>
              Use these values in <strong>WooCommerce → Splitkart</strong>. You can return here any time to re-enter the Merchant Token or reconnect the plugin.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert className="border-primary/30 bg-primary/5">
              <CheckCircle className="h-4 w-4 text-primary" />
              <AlertDescription className="text-muted-foreground">
                Your WooCommerce account is ready. Install the connector plugin in WordPress, then paste the details below.
              </AlertDescription>
            </Alert>

            <div className="grid gap-3">
              <div className="rounded-lg border bg-muted/30 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Splitkart App URL</p>
                <code className="mt-2 block break-all rounded bg-background px-3 py-2 text-sm">https://splitkart.app</code>
              </div>
              <div className="rounded-lg border bg-muted/30 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Merchant Token</p>
                <code className="mt-2 block break-all rounded bg-background px-3 py-2 text-sm">{merchantId}</code>
              </div>
              <div className="rounded-lg border bg-muted/30 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Webhook Secret</p>
                <code className="mt-2 block break-all rounded bg-background px-3 py-2 text-sm">
                  {webhookSecret || "Refresh this page to load your webhook secret"}
                </code>
              </div>
              <div className="rounded-lg border bg-muted/30 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Store URL</p>
                <code className="mt-2 block break-all rounded bg-background px-3 py-2 text-sm">{storeDomain}</code>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-sm font-medium">How it works</p>
              <div className="grid gap-3">
                <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">1</div>
                  <div>
                    <p className="text-sm font-medium">Install the Splitkart connector plugin</p>
                    <p className="text-xs text-muted-foreground">Upload the WooCommerce connector and open its settings page.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">2</div>
                  <div>
                    <p className="text-sm font-medium">Paste Merchant Token and Webhook Secret</p>
                    <p className="text-xs text-muted-foreground">These link the Woo store with this Splitkart merchant account.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">3</div>
                  <div>
                    <p className="text-sm font-medium">Save settings in WordPress</p>
                    <p className="text-xs text-muted-foreground">The Split with Friends button should appear on Woo product, cart, and mini-cart areas.</p>
                  </div>
                </div>
              </div>
            </div>

            <Alert className="border-blue-500/30 bg-blue-500/5">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-sm text-muted-foreground">
                If you ever need to save the Merchant Token again, this page remains available after login.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Switch To Shopify
            </CardTitle>
            <CardDescription>
              If you are moving this merchant from WooCommerce to Shopify, connect the Shopify store here. Splitkart will keep this merchant account and update the platform connection.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-amber-500/30 bg-amber-500/5">
              <Info className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-sm text-muted-foreground">
                After Shopify is connected, this merchant will start using Shopify billing and storefront integration. WooCommerce connector credentials on this page will no longer be the active platform setup.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <p className="text-sm font-medium">Shopify store domain</p>
              <Input
                placeholder="your-store.myshopify.com"
                value={shopifyDomainInput}
                onChange={(event) => setShopifyDomainInput(event.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
              <p className="text-xs text-muted-foreground">
                Use the permanent Shopify domain, not the public storefront domain.
              </p>
            </div>

            <Button onClick={handleConnectShopify} disabled={connectingShopify}>
              {connectingShopify ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Store className="mr-2 h-4 w-4" />}
              Connect Shopify And Switch Platform
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const storeConnected = Boolean(shopifyAccessToken);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Code className="h-5 w-5" />
          Shopify Storefront Setup
        </CardTitle>
        <CardDescription>
          Review your storefront widget status and manage how Splitkart appears on your Shopify store.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {storeConnected ? (
          <Alert className="border-primary/30 bg-primary/5">
            <CheckCircle className="h-4 w-4 text-primary" />
            <AlertDescription className="text-muted-foreground">
              Your Shopify store is connected. {widgetInstalled ? "The storefront widget is active." : "The next step is enabling the storefront widget."}
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="border-blue-500/30 bg-blue-500/5">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-muted-foreground">
              Open the app from Shopify Admin if the storefront connection needs to refresh before enabling the widget.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-3">
          <div className="flex items-start gap-3 rounded-lg border bg-primary/5 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Package className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">Cart page split button</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Adds the Split with Friends action on the cart page before checkout.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
              <Store className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">Homepage banner</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Promotes Splitkart on your storefront and can be customized from widget preferences.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Badge variant="outline" className={widgetInstalled ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
            {widgetInstalled ? "Widget active" : "Widget pending"}
          </Badge>
          <span className="text-sm text-muted-foreground">
            {widgetInstalled ? "Storefront widget is enabled." : "Use widget preferences to install or reconfigure the storefront widget."}
          </span>
        </div>

        <div className="rounded-lg border bg-muted/30 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Connected Shopify Store</p>
          <code className="mt-2 block break-all rounded bg-background px-3 py-2 text-sm">
            {storeDomain || "No Shopify domain linked yet"}
          </code>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Reconnect or change Shopify store</p>
          <Input
            placeholder="your-store.myshopify.com"
            value={shopifyDomainInput}
            onChange={(event) => setShopifyDomainInput(event.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <p className="text-xs text-muted-foreground">
            If the access token expires or you need to move this merchant to another Shopify store, reconnect it here.
          </p>
        </div>

        <Button onClick={handleConnectShopify} disabled={connectingShopify}>
          {connectingShopify ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          {storeConnected ? "Reconnect Shopify Store" : "Connect Shopify Store"}
        </Button>

        {onOpenPreferences && (
          <Button variant="outline" onClick={onOpenPreferences}>
            <ShoppingCart className="mr-2 h-4 w-4" />
            Open Widget Preferences
          </Button>
        )}

        <Button
          variant="ghost"
          onClick={() => window.open("/merchant/widget-guide", "_blank")}
        >
          <ExternalLink className="mr-2 h-4 w-4" />
          Open Manual Setup Guide
        </Button>
      </CardContent>
    </Card>
  );
}

function normalizeShopifyDomain(value: string): string {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "");

  if (!normalized) {
    return "";
  }

  if (normalized.endsWith(".myshopify.com")) {
    return normalized;
  }

  return `${normalized}.myshopify.com`;
}

function isValidShopifyDomain(value: string): boolean {
  return /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(value);
}
